import React, { useState, useEffect } from "react";
import { Card, Spinner, Alert, Button } from "react-bootstrap";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../services/firebase";
import {
  BsHouseDoor,
  BsCheckCircle,
  BsInfoCircle,
  BsArrowLeft,
  BsCalendar,
  BsPerson,
  BsFileText,
  BsTelephone,
  BsEnvelope,
  BsFileEarmarkText,
  BsPauseCircle,
  BsWallet,
  BsCreditCard,
  BsExclamationCircle,
  BsTelephoneFill,
  BsFileEarmarkPlus,
  BsBuilding,
  BsArrowLeftRight,
  BsXCircle,
  BsStopCircle,
  BsLockFill,
} from "react-icons/bs";
import "./TimeshareDetail.css";

const TimeshareDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [timeshare, setTimeshare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [vendors, setVendors] = useState({});
  const [vendorsLoading, setVendorsLoading] = useState(true);

  // Stage descriptions from manual data
  const stageDescriptions = {
    Rescission:
      "We're in the cancellation period specified in our agreement. If you need any assistance, we're here to help!",
    Cancelled:
      "Your file has been cancelled. If you'd like to restart or need any further assistance, please don't hesitate to reach out. We're here to support you.",
    "TF Hold":
      "Your property is currently on hold due to pending reservations. We're unable to start your file until the transfer hold is lifted. Once it's clear, we'll jump right back into action!",
    Pender:
      "We're waiting for payment completion on your end before we can proceed with your file. Once everything is set, we'll continue working diligently to help you with your timeshare exit.",
    IHF: "You're on an In-House Financing (IHF) or COD deal, and we'll start processing your file once 50% of the payment has been made. Thank you for working with us!",
    "Welcome Call":
      "We've completed your welcome call to review the details of your file. You'll also receive a follow-up welcome email with helpful information and your vendor's contact details. We're excited to start this journey with you!",
    "Docs Requested":
      "We've sent you the packet for your timeshare exit file. Please review and sign, as this is a necessary step for us to move forward. We're here if you need assistance with this!",
    "Vendor Status #1":
      "We've received your signed documents, and your file is now with the vendor. The vendor is working diligently to resolve your case. Keep an eye out for their updates, and feel free to contact us if you have any questions or need an update. You can also chat with us via the portal or book an appointment with us online!",
    "Vendor Status #2":
      "Your vendor is actively working on your file. They'll be reaching out with updates soon. Rest assured, we're on track to help you through this process.",
    "Vendor Status #3":
      "Your vendor has begun work on your file, and things are moving forward! We're keeping an eye on everything to make sure things stay on track. Stay tuned for updates from your vendor.",
    "Vendor Status #4":
      "We've made great progress on your file, and your vendor is continuing to work on it. You'll receive an update from them shortly. As always, we're here if you need us!",
    "Vendor Status #5":
      "Your vendor is hard at work to resolve your timeshare exit. We're monitoring the situation to ensure things stay on schedule. Expect to hear from them soon.",
    "Vendor Status #6":
      "We're working closely with your vendor to move your file forward. They're on track, and updates will be shared with you soon. We're here for any questions you might have!",
    "Reassigned Vendor":
      "We've reassigned your file to a new vendor who can handle your timeshare exit more effectively. The process is still progressing, and you'll receive updates shortly.",
    "Non Compliant":
      "We're currently missing some important documents from you, which is delaying progress. Please contact our Release Department so we can proceed with your timeshare exit. We're here to help you get back on track!",
    "CC Dispute":
      "Your file is temporarily on hold while we handle the credit card chargeback process. Once that's resolved, we'll resume working on your file. We appreciate your patience as we navigate this!",
    "Work Stop":
      "Work on your file has been paused. This may be due to client-initiated actions or other factors. We'll be happy to discuss how we can proceed and get things back on track.",
    Closed:
      "Your file has been closed. If you need anything further or have any questions, feel free to reach out to us anytime. We're happy to assist you!",
    Completed:
      "Congratulations! Your timeshare exit is complete. You've successfully been exited from your timeshare, and we're thrilled to have helped you through this process. Enjoy the next chapter of your journey! If you're happy with our service, we'd greatly appreciate it if you could leave us a review on Google: ",
  };

  const getStageIcon = (stage) => {
    if (!stage) return <BsInfoCircle />; // Default icon for null or undefined stage

    const stageKey = stage.toLowerCase();

    if (stageKey === "rescission") return <BsFileEarmarkText />;
    if (stageKey === "tf hold") return <BsPauseCircle />;
    if (stageKey === "pender") return <BsWallet />;
    if (stageKey === "ihf") return <BsCreditCard />;
    if (stageKey === "non compliant") return <BsExclamationCircle />;
    if (stageKey === "cc dispute") return <BsCreditCard />;
    if (stageKey === "welcome call") return <BsTelephoneFill />;
    if (stageKey === "docs requested") return <BsFileEarmarkPlus />;
    if (stageKey === "vendor") return <BsBuilding />;
    if (stageKey === "reassigned vendor") return <BsArrowLeftRight />;
    if (stageKey === "cancelled") return <BsXCircle />;
    if (stageKey === "work stop") return <BsStopCircle />;
    if (stageKey === "completed") return <BsCheckCircle />;
    if (stageKey === "closed") return <BsLockFill />;

    return <BsInfoCircle />;
  };

  const getStageVariant = (stage) => {
    if (!stage) return "secondary"; // Default case for null or undefined stage

    const stageKey = stage.toLowerCase();

    if (stageKey === "rescission") return "warning";
    if (stageKey === "tf hold") return "warning";
    if (stageKey === "pender") return "warning";
    if (stageKey === "ihf") return "primary";
    if (stageKey === "non compliant") return "danger";
    if (stageKey === "cc dispute") return "danger";
    if (stageKey === "welcome call") return "primary";
    if (stageKey === "docs requested") return "primary";
    if (stageKey === "vendor") return "success";
    if (stageKey === "reassigned vendor") return "success";
    if (stageKey === "cancelled") return "danger";
    if (stageKey === "work stop") return "danger";
    if (stageKey === "completed") return "success";
    if (stageKey === "closed") return "secondary";

    return "secondary";
  };

  useEffect(() => {
    if (!id) {
      setError("Timeshare ID is required");
      setLoading(false);
      return;
    }

    const fetchTimeshare = async () => {
      try {
        const timeshareDoc = await getDoc(doc(db, "timeshares", id));
        if (timeshareDoc.exists()) {
          setTimeshare({ id: timeshareDoc.id, ...timeshareDoc.data() });
        } else {
          setError("Timeshare not found");
        }
      } catch (err) {
        setError("Failed to load timeshare details");
      } finally {
        setLoading(false);
      }
    };

    const fetchVendors = async () => {
      try {
        const vendorsCollection = await getDocs(collection(db, "vendors"));
        const vendorsMap = {};
        vendorsCollection.forEach((doc) => {
          const vendorData = doc.data();
          vendorsMap[vendorData.name] = vendorData;
        });
        setVendors(vendorsMap);
      } catch (err) {
        console.error("Failed to load vendors:", err);
      } finally {
        setVendorsLoading(false);
      }
    };

    fetchTimeshare();
    fetchVendors();
  }, [id]);

  const LoadingState = () => (
    <div className="timeshare-detail-loading">
      <div className="loading-content">
        <Spinner animation="border" variant="primary" size="lg" />
        <h5 className="mt-3">Loading Timeshare Details...</h5>
        <p className="text-muted">Please wait while we fetch the information</p>
      </div>
    </div>
  );

  const ErrorState = () => (
    <Alert variant="danger" className="timeshare-detail-error">
      <Alert.Heading>Unable to Load Timeshare Details</Alert.Heading>
      <p>{error}</p>
      <Button
        variant="outline-danger"
        onClick={() => navigate("/dashboard/timeshares")}
      >
        Back to Timeshares
      </Button>
    </Alert>
  );

  if (loading) return <LoadingState />;
  if (error) return <ErrorState />;
  if (!timeshare) return <ErrorState />;

  // Calculate vendor status based on months since creation
  const calculateVendorStatus = (createdAt) => {
    if (!createdAt) return "Vendor Status #1";

    const now = new Date(
      new Date().toLocaleString("en-US", { timeZone: "America/Chicago" })
    );
    const createdDate = createdAt.toDate();
    const monthsDiff =
      (now.getFullYear() - createdDate.getFullYear()) * 12 +
      (now.getMonth() - createdDate.getMonth());

    if (monthsDiff < 3) return "Vendor Status #1";
    if (monthsDiff < 6) return "Vendor Status #2";
    if (monthsDiff < 9) return "Vendor Status #3";
    if (monthsDiff < 12) return "Vendor Status #4";
    if (monthsDiff < 15) return "Vendor Status #5";
    return "Vendor Status #6";
  };

  // Determine current stage
  let currentStage = timeshare.pipelineStage;

  // If stage is "Vendor", calculate the specific vendor status
  if (currentStage === "Vendor" && timeshare.vendorStageAt) {
    currentStage = calculateVendorStatus(timeshare.vendorStageAt);
  }

  const stageDescription =
    stageDescriptions[currentStage] || "Stage description not available.";

  // Get vendor information if vendorName exists
  const vendorInfo = timeshare.vendorName
    ? vendors[timeshare.vendorName]
    : null;

  return (
    <div className="timeshare-detail-container">
      <div className="detail-header">
        <Button
          variant="link"
          className="back-button"
          onClick={() => navigate("/dashboard/timeshares")}
        >
          <BsArrowLeft /> Back to Timeshares
        </Button>
        <h2 className="detail-title">
          {timeshare.opportunityName || "Timeshare Property"}
        </h2>
      </div>

      <div className="detail-content">
        {/* Current Stage Section */}
        <Card className="main-detail-card mb-4">
          <Card.Body>
            <div className="stage-section">
              <div className="stage-header">
                <h4>Current Stage</h4>
                <div
                  className={`stage-badge-large ${getStageVariant(
                    currentStage
                  )}`}
                >
                  {getStageIcon(currentStage)}
                  <span>{currentStage}</span>
                </div>
              </div>

              <div className="description-section">
                <h5>Stage Description</h5>
                <div className="description-content">
                  {currentStage === "Completed" ? (
                    <p>
                      {stageDescription}
                      <a
                        href="https://g.co/kgs/7fn82oD"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#007bff",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                      >
                        Leave a Review
                      </a>
                    </p>
                  ) : (
                    <p>{stageDescription}</p>
                  )}
                </div>
              </div>
            </div>

            {timeshare.description && (
              <div className="additional-info-section">
                <h5>Additional Information</h5>
                <p>{timeshare.description}</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Timeshare Information Section */}
        <Card className="info-card">
          <Card.Body>
            <h5 className="info-title">Timeshare Information</h5>

            <div className="info-item">
              <BsHouseDoor className="info-icon" />
              <div className="info-content">
                <span className="info-label">Property</span>
                <span className="info-value">
                  {timeshare.opportunityName || "N/A"}
                </span>
              </div>
            </div>

            <div className="info-item">
              <BsCalendar className="info-icon" />
              <div className="info-content">
                <span className="info-label">Last Updated</span>
                <span className="info-value">
                  {timeshare.updatedAt?.toDate().toLocaleDateString() || "N/A"}
                </span>
              </div>
            </div>

            {timeshare.createdAt && (
              <div className="info-item">
                <BsCalendar className="info-icon" />
                <div className="info-content">
                  <span className="info-label">Created</span>
                  <span className="info-value">
                    {timeshare.createdAt.toDate().toLocaleDateString()}
                  </span>
                </div>
              </div>
            )}

            {timeshare.contactName && (
              <div className="info-item">
                <BsPerson className="info-icon" />
                <div className="info-content">
                  <span className="info-label">Contact</span>
                  <span className="info-value">{timeshare.contactName}</span>
                </div>
              </div>
            )}

            {timeshare.contactEmail && (
              <div className="info-item">
                <BsEnvelope className="info-icon" />
                <div className="info-content">
                  <span className="info-label">Email</span>
                  <span className="info-value">{timeshare.contactEmail}</span>
                </div>
              </div>
            )}

            {timeshare.contactPhone && (
              <div className="info-item">
                <BsTelephone className="info-icon" />
                <div className="info-content">
                  <span className="info-label">Phone</span>
                  <span className="info-value">{timeshare.contactPhone}</span>
                </div>
              </div>
            )}

            {timeshare.fileNumber && (
              <div className="info-item">
                <BsFileText className="info-icon" />
                <div className="info-content">
                  <span className="info-label">File Number</span>
                  <span className="info-value">{timeshare.fileNumber}</span>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Vendor Information Section */}
        {vendorInfo && (
          <Card className="info-card mt-4">
            <Card.Body>
              <h5 className="info-title">Vendor Information</h5>

              <div className="info-item">
                <BsPerson className="info-icon" />
                <div className="info-content">
                  <span className="info-label">Vendor Name</span>
                  <span className="info-value">{vendorInfo.name}</span>
                </div>
              </div>

              {vendorInfo.address && (
                <div className="info-item">
                  <BsHouseDoor className="info-icon" />
                  <div className="info-content">
                    <span className="info-label">Address</span>
                    <span className="info-value">{vendorInfo.address}</span>
                  </div>
                </div>
              )}

              {vendorInfo.phone && (
                <div className="info-item">
                  <BsTelephone className="info-icon" />
                  <div className="info-content">
                    <span className="info-label">Phone</span>
                    <span className="info-value">{vendorInfo.phone}</span>
                  </div>
                </div>
              )}

              {vendorInfo.fax && (
                <div className="info-item">
                  <BsTelephone className="info-icon" />
                  <div className="info-content">
                    <span className="info-label">Fax</span>
                    <span className="info-value">{vendorInfo.fax}</span>
                  </div>
                </div>
              )}

              {vendorInfo.pointOfContact && (
                <div className="info-item">
                  <BsPerson className="info-icon" />
                  <div className="info-content">
                    <span className="info-label">Point of Contact</span>
                    <span className="info-value">
                      {vendorInfo.pointOfContact}
                    </span>
                  </div>
                </div>
              )}

              {vendorInfo.emails && vendorInfo.emails.length > 0 && (
                <div className="info-item">
                  <BsEnvelope className="info-icon" />
                  <div className="info-content">
                    <span className="info-label">
                      Email{vendorInfo.emails.length > 1 ? "s" : ""}
                    </span>
                    <div className="vendor-emails">
                      {vendorInfo.emails.map((email, index) => (
                        <span key={index} className="info-value vendor-email">
                          {email}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TimeshareDetail;
